import { isDefined } from "@clipboard-health/util-ts";
import { CircularProgress, Box, Stack, TextField, Button } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import axios from "axios";
import { useState } from "react";
import { type WorkplaceProfileResponse } from "@clipboard-health/contract-backend-main";
import { useUpdateChartingRecall } from "@src/appV2/FacilityProfile/api/useUpdateChartingRecall";
import { SettingsItem } from "./SettingsItem";
import { SettingsItemTitle } from "./SettingsItemTitle";
import { DEFAULT_RECALL_GRACE_PERIOD_DURATION_IN_HOURS } from "@src/appV2/Charting/constants";

interface ChartingRecallSettingProps {
  workplace: WorkplaceProfileResponse;
  isAdmin: boolean;
  onPreferencesChanged: () => void;
}

export function ChartingRecallSetting(props: ChartingRecallSettingProps) {
  const { workplace, onPreferencesChanged, isAdmin } = props;
  const [selectedChartingRecallEnabled, setSelectedChartingRecallEnabled] = useState<boolean>(
    workplace?.data.attributes.chartingRecall?.enabled ?? false
  );
  const [selectedRecallGracePeriodDurationInHours, setSelectedRecallGracePeriodDurationInHours] =
    useState(
      String(
        workplace?.data.attributes.chartingRecall?.recallGracePeriodDurationInHours ??
          DEFAULT_RECALL_GRACE_PERIOD_DURATION_IN_HOURS
      )
    );

  const { showSuccessToast } = useToast();

  const { isLoading: isUpdatingChartingRecall, mutateAsync: patchChartingRecall } =
    useUpdateChartingRecall();

  const isGracePeriodSettingVisible = selectedChartingRecallEnabled && isAdmin;

  async function updateChartingRecall(chartingRecall: {
    enabled: boolean;
    recallGracePeriodDurationInHours: number;
  }) {
    const workplaceId = workplace.data.id;
    const previousChartingRecallEnabled = selectedChartingRecallEnabled;
    setSelectedChartingRecallEnabled(chartingRecall.enabled);

    try {
      await patchChartingRecall({
        workplaceId,
        chartingRecall,
      });

      showSuccessToast("Charting recall preferences updated successfully");
      onPreferencesChanged();
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && isDefined(error.response)) {
        showErrorToast(error.response.data);
      } else {
        showErrorToast("Error updating charting recall preferences. Please, try again later");
      }

      setSelectedChartingRecallEnabled(previousChartingRecallEnabled);
    }
  }

  return (
    <Stack position="relative" sx={{ opacity: isUpdatingChartingRecall ? 0.5 : 1 }}>
      {isUpdatingChartingRecall ? (
        <Box position="absolute" top="calc(50% - 24px)" left="calc(50% - 24px)">
          <CircularProgress size={48} />
        </Box>
      ) : null}
      <SettingsItem
        key="chartingRecallEnabled"
        isLoading={isUpdatingChartingRecall}
        item={{
          key: "chartingRecallEnabled",
          title: "Charting Compliance",
          subtitle:
            "When enabled, professionals can be recalled to complete charting. Professionals with incomplete charting will also be blocked from booking at your workplace.",
          checked: selectedChartingRecallEnabled,
          onChange: (checked: boolean) =>
            updateChartingRecall({
              enabled: checked,
              recallGracePeriodDurationInHours:
                workplace.data.attributes.chartingRecall?.recallGracePeriodDurationInHours ??
                DEFAULT_RECALL_GRACE_PERIOD_DURATION_IN_HOURS,
            }),
        }}
      />
      {isGracePeriodSettingVisible && (
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            paddingY: 3,
            "&:hover": {
              backgroundColor: "#fafafa",
            },
          }}
        >
          <Stack direction="column" spacing={0}>
            <SettingsItemTitle
              title="Charting Compliance Grace Period"
              subtitle="The duration in hours a professional has to return and complete charting."
            />
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              minWidth: "200px",
            }}
            justifyContent="flex-end"
          >
            <TextField
              type="number"
              value={selectedRecallGracePeriodDurationInHours}
              onChange={(e) => setSelectedRecallGracePeriodDurationInHours(e.target.value)}
              disabled={isUpdatingChartingRecall}
              sx={{ width: "100px" }}
              size="small"
              inputProps={{ min: 1 }}
            />
            <Button
              variant="text"
              color="primary"
              onClick={() =>
                updateChartingRecall({
                  enabled: true,
                  recallGracePeriodDurationInHours: Number(
                    selectedRecallGracePeriodDurationInHours
                  ),
                })
              }
              disabled={
                isUpdatingChartingRecall || Number(selectedRecallGracePeriodDurationInHours) < 1
              }
            >
              {isUpdatingChartingRecall ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
